import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const money = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  if (typeof n !== 'number') return String(n);
  return `$${Math.round(n).toLocaleString()}`;
};

const signedMoney = (n) => {
  if (!n) return '$0';
  const sign = n > 0 ? '+' : '−';
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString()}`;
};

const formatTs = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const SnapshotTile = ({ label, value, accent }) => (
  <Box sx={{ flex: 1, minWidth: 0, p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color: accent }}>
      {money(value)}
    </Typography>
  </Box>
);

const VarianceHistoryDialog = ({ open, entry, dealerName, bac, onClose, onApplyTemplate }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  if (!entry) return null;

  const snapshot = entry.snapshotTotals || {};
  const thread = entry.noteThread || [];
  const tickets = entry.relatedTickets || [];

  const handleApply = () => {
    onApplyTemplate?.(entry);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #6a1b9a 100%)' }} />
      <DialogTitle sx={{ pr: 6, pt: 2.5 }}>
        <Stack spacing={0.5}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6, fontSize: 10.5 }}
          >
            Prior variance · {entry.serviceMonth} {new Date(entry.date).getFullYear()}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15, letterSpacing: -0.5 }}>
            {dealerName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', fontWeight: 700 }}
            >
              BAC {bac}
            </Typography>
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Resolved {entry.date} by {entry.reviewedBy}
            </Typography>
          </Stack>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 12 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Category / remediation / delta */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, flex: 1 }}>
              <Chip label={entry.category} variant="outlined" />
              <Chip label={entry.remediation} variant="outlined" color="primary" />
            </Stack>
            <Box sx={{ textAlign: { sm: 'right' } }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Max |Δ|
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                {signedMoney(entry.maxDelta)}
              </Typography>
            </Box>
          </Stack>

          {/* Snapshot at time of reconciliation */}
          {(snapshot.gm !== undefined || snapshot.cpq !== undefined || snapshot.dpe !== undefined) && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                Snapshot at time of reconciliation
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                <SnapshotTile label="GM" value={snapshot.gm} accent="primary.main" />
                <SnapshotTile label="CPQ" value={snapshot.cpq} accent="secondary.main" />
                <SnapshotTile label="DPE" value={snapshot.dpe} accent="info.main" />
              </Stack>
            </Paper>
          )}

          {/* Summary */}
          {entry.notes && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>
                Summary
              </Typography>
              <Typography variant="body2">{entry.notes}</Typography>
            </Paper>
          )}

          {/* Note thread */}
          {thread.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                Investigation thread ({thread.length})
              </Typography>
              <Stack spacing={1.5}>
                {thread.map((n, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 1.25,
                      borderLeft: '3px solid',
                      borderColor: 'primary.light',
                      bgcolor: 'action.hover',
                      borderRadius: '0 4px 4px 0',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {n.author}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatTs(n.ts)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2">{n.text}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Related tickets */}
          {tickets.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                Related tickets
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {tickets.map((t) => (
                  <Link
                    key={t}
                    href={`#/tickets/${t}`}
                    underline="none"
                    sx={{ display: 'inline-flex' }}
                  >
                    <Chip
                      size="small"
                      icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      label={`#${t}`}
                      clickable
                      variant="outlined"
                    />
                  </Link>
                ))}
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Mock links only — these will route to your ticket system in production.
              </Typography>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Fill the current variance's Category + Remediation with these values">
          <Button
            onClick={handleApply}
            variant="contained"
            startIcon={<ContentCopyIcon />}
          >
            Use as template
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VarianceHistoryDialog;
